import React, { useState, useEffect, useCallback, useRef } from "react";
import { apiGet, apiDelete } from "../../api";
import { SvgIcon } from "../SvgIcon";
import { ConfirmPasswordModal } from "../ConfirmPasswordModal";
import { CardSetupModal } from "./CardSetupModal";
import { RecoveryEmailModal } from "./RecoveryEmailModal";
import { RecoveryPhoneModal } from "./RecoveryPhoneModal";
import { SecurityQuestionModal } from "./SecurityQuestionModal";
import { t, Locale } from "../../i18n";

// A single recovery DESTINATION (where a recovery result is delivered), as
// returned by GET /v1/account/recovery/details. Model A supports multiple
// destinations per type; the UI renders them all.
interface Destination {
  id: string;
  type: "email" | "sms";
  maskedValue: string;
  verified: boolean;
  verifiedAt: string | null;
}

interface RecoveryAttempt {
  id: string;
  date: string;
  method: string;
  status: "completed" | "cancelled" | "expired";
  location: string;
  isAnomalous: boolean;
}

interface CardAnchor {
  status: "active" | "inactive";
  brand?: string;
  last4?: string;
}

interface IdentityAnchor {
  status: "verified" | "not-verified";
  verifiedDate?: string;
}

interface RecoveryStatusResponse {
  codesCount: number;
  hasRecoveryEmail: boolean;
  hasSecurityQuestion: boolean;
  hasCard: boolean;
  identityVerified: boolean;
  hasPhone: boolean;
}

// Phase 5 display shape: the destination LIST + card/identity method fields.
interface RecoveryDetailsResponse {
  destinations: Destination[];
  cardLast4: string | null;
  cardExpiryMM: number | null;
  cardExpiryYYYY: number | null;
  identityVerifiedAt: string | null;
}

interface RecoveryHistoryResponse {
  attempts: Array<{
    id: string;
    timestamp: string;
    method: string;
    status: "completed" | "cancelled" | "expired";
    location: string;
    isAnomalous: boolean;
  }>;
}

// The three post-recovery hold tiers the backend can return on a mutation.
type HoldTier = "settings" | "touch_factors" | "read_sensitive";

interface HoldNotice {
  tier: HoldTier;
  until: string | null;
}

function getStatusColor(status: "completed" | "cancelled" | "expired"): string {
  switch (status) {
    case "completed":
      return "#10b981";
    case "cancelled":
    case "expired":
      return "var(--muted)";
  }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

// Inspect an API error body for the post-recovery hold 403 and, if present,
// return a HoldNotice. The backend shape is:
//   403 { error: "recovery.hold_active", tier, until }
// where tier ∈ { settings, touch_factors, read_sensitive } and until is an ISO
// string (or null). Returns null for any non-hold error.
function parseHoldError(body: unknown): HoldNotice | null {
  if (!body || typeof body !== "object") return null;
  const b = body as Record<string, unknown>;
  if (b.error !== "recovery.hold_active") return null;
  const tier = b.tier;
  const validTier: HoldTier =
    tier === "touch_factors" || tier === "read_sensitive" ? tier : "settings";
  return {
    tier: validTier,
    until: typeof b.until === "string" ? b.until : null
  };
}

export function RecoveryMethods(props: { locale: Locale }) {
  const locale = props.locale;

  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [hasSecurityQuestion, setHasSecurityQuestion] = useState(false);
  const [cardAnchor, setCardAnchor] = useState<CardAnchor>({ status: "inactive" });
  const [identityAnchor, setIdentityAnchor] = useState<IdentityAnchor>({ status: "not-verified" });
  const [attempts, setAttempts] = useState<RecoveryAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  // null = loading, false = free, true = paid
  const [isPaidPlan, setIsPaidPlan] = useState<boolean | null>(null);

  // Post-recovery hold banner (set when a mutation returns 403 hold_active).
  const [holdNotice, setHoldNotice] = useState<HoldNotice | null>(null);

  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  // Destination pending a password-confirmed removal (null when no modal open).
  const [removeTarget, setRemoveTarget] = useState<Destination | null>(null);

  const loadData = useCallback(async (signal?: AbortSignal) => {
    const [statusRes, detailsRes, historyRes, planRes] = await Promise.all([
      apiGet<RecoveryStatusResponse>("/v1/account/recovery", { signal }),
      apiGet<RecoveryDetailsResponse>("/v1/account/recovery/details", { signal }),
      apiGet<RecoveryHistoryResponse>("/v1/account/recovery/history?limit=10", { signal }),
      apiGet<{ subscription: unknown | null }>("/v1/payments/status", { signal })
    ]);

    if (signal?.aborted) return;

    // Treat any active subscription as paid; free/trial/none = free
    if (planRes.ok) {
      setIsPaidPlan(!!planRes.data.subscription);
    }

    if (statusRes.ok && detailsRes.ok) {
      const s = statusRes.data;
      const d = detailsRes.data;

      setDestinations(Array.isArray(d.destinations) ? d.destinations : []);
      setHasSecurityQuestion(s.hasSecurityQuestion);

      setCardAnchor({
        status: s.hasCard ? "active" : "inactive",
        last4: d.cardLast4 ?? undefined
      });

      setIdentityAnchor({
        status: s.identityVerified ? "verified" : "not-verified",
        verifiedDate: d.identityVerifiedAt ? formatDate(d.identityVerifiedAt) : undefined
      });
    }

    if (historyRes.ok) {
      setAttempts(
        historyRes.data.attempts.slice(0, 10).map((a) => ({
          id: a.id,
          date: formatDate(a.timestamp),
          method: a.method,
          status: a.status,
          location: a.location,
          isAnomalous: a.isAnomalous
        }))
      );
    }

    setLoading(false);
  }, []);

  // Initial load
  useEffect(() => {
    const ac = new AbortController();
    loadData(ac.signal);
    return () => ac.abort();
  }, [loadData]);

  // Poll recovery status every 10s to detect verification in another tab.
  // When the verified-email or verified-phone state flips, do a full reload so
  // the destination list (masked values, badges) stays in sync.
  const prevVerifiedSig = useRef<string>("");
  useEffect(() => {
    const intervalMs = 10_000;
    const tick = async () => {
      const res = await apiGet<RecoveryStatusResponse>("/v1/account/recovery");
      if (!res.ok) return;
      const sig = `${res.data.hasRecoveryEmail}|${res.data.hasPhone}`;
      if (prevVerifiedSig.current && sig !== prevVerifiedSig.current) {
        loadData();
      }
      prevVerifiedSig.current = sig;
    };

    const timer = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(timer);
  }, [loadData]);

  // Remove a specific destination by id. Passes the id in the DELETE body so
  // only that row is removed (Model A multi-destination). Surfaces the hold
  // banner if the mutation is blocked by the post-recovery hold.
  async function handleRemoveDestination(dest: Destination) {
    const path =
      dest.type === "email" ? "/v1/account/recovery/email" : "/v1/account/recovery/phone";
    const res = await apiDelete<{ ok: boolean }>(path, { id: dest.id });
    if (res.ok) {
      setDestinations((prev) => prev.filter((d) => d.id !== dest.id));
      return;
    }
    const hold = parseHoldError(res.body);
    if (hold) {
      setHoldNotice(hold);
    }
    // Refresh so the list reflects the true server state after any failure.
    loadData();
  }

  if (loading) {
    return (
      <div style={{ padding: "24px 0", color: "var(--muted)", fontSize: "13px" }}>
        {t(locale, "recoveryMethods.loading")}
      </div>
    );
  }

  const emailDestinations = destinations.filter((d) => d.type === "email");
  const phoneDestinations = destinations.filter((d) => d.type === "sms");
  const verifiedCount = destinations.filter((d) => d.verified).length;

  // Localized "recovery strength" nudge (recommend, never enforce).
  let strengthKey: string;
  const strengthVars: Record<string, string> = {};
  if (verifiedCount === 0) {
    strengthKey = "recoveryMethods.strength.none";
  } else if (verifiedCount === 1) {
    strengthKey = "recoveryMethods.strength.single";
  } else {
    strengthKey = "recoveryMethods.strength.good";
    strengthVars.count = String(verifiedCount);
  }
  const strengthColor = verifiedCount === 0 ? "#f59e0b" : "var(--muted)";

  function holdMessage(notice: HoldNotice): string {
    const date = formatDate(notice.until);
    if (notice.tier === "read_sensitive") {
      return date
        ? t(locale, "recoveryMethods.hold.readSensitive", { date })
        : t(locale, "recoveryMethods.hold.messageNoDate");
    }
    if (notice.tier === "touch_factors") {
      return date
        ? t(locale, "recoveryMethods.hold.touchFactors", { date })
        : t(locale, "recoveryMethods.hold.messageNoDate");
    }
    return date
      ? t(locale, "recoveryMethods.hold.message", { date })
      : t(locale, "recoveryMethods.hold.messageNoDate");
  }

  const btnGhost: React.CSSProperties = {
    background: "transparent",
    color: "var(--text)",
    border: "1px solid var(--control-border)",
    padding: "6px 12px",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: 500
  };
  const btnAccent: React.CSSProperties = {
    background: "var(--accent)",
    color: "white",
    border: "none",
    padding: "6px 12px",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: 500
  };
  const btnDanger: React.CSSProperties = {
    background: "transparent",
    color: "#ef4444",
    border: "1px solid #ef4444",
    padding: "6px 12px",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: 500
  };

  function renderDestinationRow(dest: Destination) {
    return (
      <div
        key={dest.id}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 0",
          borderBottom: "1px solid var(--line)"
        }}
      >
        <div style={{ fontSize: "12px", color: "var(--muted)", display: "flex", alignItems: "center", gap: "6px" }}>
          <span style={{ color: "var(--text)" }}>{dest.maskedValue}</span>
          {dest.verified ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", fontSize: "11px", fontWeight: 600, color: "var(--success, #10b981)", background: "rgba(16, 185, 129, 0.1)", padding: "2px 6px", borderRadius: "3px" }}>
              <span style={{ display: "inline-flex", width: 12, height: 12, color: "var(--success, #10b981)" }}>
                <SvgIcon name="check-circle" title="Verified" />
              </span>
              {t(locale, "recoveryMethods.verified")}
            </span>
          ) : (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", fontSize: "11px", fontWeight: 600, color: "#f59e0b", background: "rgba(245, 158, 11, 0.1)", padding: "2px 6px", borderRadius: "3px" }}>
              <span style={{ display: "inline-flex", width: 12, height: 12, color: "#f59e0b" }}>
                <SvgIcon name="clock-history" title="Pending" />
              </span>
              {t(locale, "recoveryMethods.pendingVerification")}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {!dest.verified && (
            <button
              onClick={() => (dest.type === "email" ? setEmailModalOpen(true) : setPhoneModalOpen(true))}
              style={btnGhost}
            >
              {t(locale, "recoveryMethods.destination.verify")}
            </button>
          )}
          <button
            onClick={() => setRemoveTarget(dest)}
            style={btnDanger}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(239, 68, 68, 0.1)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            {t(locale, "common.remove")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 style={{ marginTop: 0, fontSize: "15px", fontWeight: 600, marginBottom: "16px" }}>{t(locale, "recoveryMethods.title")}</h3>

      {/* Explanatory text at top */}
      <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "16px", lineHeight: "1.5" }}>
        {t(locale, "recoveryMethods.description")}
      </div>

      {/* Post-recovery hold banner — shown when a mutation is blocked. */}
      {holdNotice && (
        <div
          role="alert"
          style={{
            fontSize: "12px",
            color: "var(--text)",
            background: "rgba(245, 158, 11, 0.1)",
            border: "1px solid rgba(245, 158, 11, 0.4)",
            borderRadius: "4px",
            padding: "10px 12px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "flex-start",
            gap: "8px"
          }}
        >
          <span style={{ display: "inline-flex", width: 14, height: 14, color: "#f59e0b", flexShrink: 0, marginTop: "1px" }}>
            <SvgIcon name="clock-history" title={t(locale, "recoveryMethods.hold.title")} />
          </span>
          <div style={{ flex: 1, lineHeight: "1.5" }}>
            <div style={{ fontWeight: 600, marginBottom: "2px" }}>{t(locale, "recoveryMethods.hold.title")}</div>
            <div style={{ color: "var(--muted)" }}>{holdMessage(holdNotice)}</div>
          </div>
          <button
            onClick={() => setHoldNotice(null)}
            style={{ background: "transparent", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: "12px", fontWeight: 500, padding: "0", flexShrink: 0 }}
          >
            {t(locale, "recoveryMethods.hold.dismiss")}
          </button>
        </div>
      )}

      {/* Recovery strength nudge (recommend, never enforce). */}
      <div
        style={{
          fontSize: "12px",
          color: strengthColor,
          background: verifiedCount === 0 ? "rgba(245, 158, 11, 0.08)" : "var(--surface, transparent)",
          border: verifiedCount === 0 ? "1px solid rgba(245, 158, 11, 0.3)" : "1px solid var(--line)",
          borderRadius: "4px",
          padding: "10px 12px",
          marginBottom: "20px",
          lineHeight: "1.5"
        }}
      >
        {t(locale, strengthKey, strengthVars)}
      </div>

      {/* ── Recovery email destinations ─────────────────────────────────── */}
      <div style={{ marginBottom: "24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
          <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)" }}>{t(locale, "recoveryMethods.email.sectionTitle")}</div>
          <button onClick={() => setEmailModalOpen(true)} style={btnAccent} onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")} onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}>
            {t(locale, "recoveryMethods.email.addAnother")}
          </button>
        </div>
        <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "8px", lineHeight: "1.5" }}>
          {t(locale, "recoveryMethods.email.sectionDescription")}
        </div>
        {emailDestinations.length === 0 ? (
          <div style={{ fontSize: "12px", color: "var(--muted)", padding: "8px 0" }}>
            {t(locale, "recoveryMethods.email.none")}
          </div>
        ) : (
          <div>{emailDestinations.map(renderDestinationRow)}</div>
        )}
      </div>

      {/* ── Recovery phone destinations ─────────────────────────────────── */}
      <div style={{ marginBottom: "24px", paddingBottom: "16px", borderBottom: "1px solid var(--line)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
          <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)" }}>{t(locale, "recoveryMethods.phone.sectionTitle")}</div>
          <button onClick={() => setPhoneModalOpen(true)} style={btnAccent} onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")} onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}>
            {t(locale, "recoveryMethods.phone.addAnother")}
          </button>
        </div>
        <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "8px", lineHeight: "1.5" }}>
          {t(locale, "recoveryMethods.phone.sectionDescription")}
        </div>
        {phoneDestinations.length === 0 ? (
          <div style={{ fontSize: "12px", color: "var(--muted)", padding: "8px 0" }}>
            {t(locale, "recoveryMethods.phone.none")}
          </div>
        ) : (
          <div>{phoneDestinations.map(renderDestinationRow)}</div>
        )}
      </div>

      {/* ID / Passport Verification (method, not destination) */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: "16px", borderBottom: "1px solid var(--line)", marginBottom: "16px" }}>
        <div>
          <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)", marginBottom: "4px" }}>{t(locale, "recoveryMethods.identity.title")}</div>
          <div style={{ fontSize: "12px", color: "var(--muted)" }}>
            {identityAnchor.status === "verified"
              ? t(locale, "recoveryMethods.identity.verifiedOn", { date: identityAnchor.verifiedDate || "" })
              : t(locale, "recoveryMethods.notVerified")}
          </div>
        </div>
        {identityAnchor.status === "verified" ? (
          <button style={btnGhost}>
            {t(locale, "common.update")}
          </button>
        ) : (
          <button style={btnAccent} onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")} onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}>
            {t(locale, "common.verify")}
          </button>
        )}
      </div>

      {/* Credit Card Verification — only shown for paid-plan users (method) */}
      {isPaidPlan && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: "16px", borderBottom: "1px solid var(--line)", marginBottom: "16px" }}>
          <div>
            <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)", marginBottom: "4px" }}>{t(locale, "recoveryMethods.card.title")}</div>
            <div style={{ fontSize: "12px", color: "var(--muted)" }}>
              {cardAnchor.status === "active"
                ? `${cardAnchor.brand ? cardAnchor.brand + " " : ""}${t(locale, "recoveryMethods.card.ending", { last4: cardAnchor.last4 || "" })}`
                : t(locale, "recoveryMethods.card.noCard")}
            </div>
          </div>
          <button
            onClick={() => setCardModalOpen(true)}
            style={btnAccent}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            {cardAnchor.status === "active" ? t(locale, "common.change") : t(locale, "recoveryMethods.card.add")}
          </button>
        </div>
      )}

      {/* Speed up recovery divider */}
      <div style={{ height: "1px", background: "var(--line)", margin: "16px 0", opacity: 0.5 }} />

      {/* Speed up recovery subsection */}
      <div style={{ marginTop: "16px", marginBottom: "16px" }}>
        <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted)", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          {t(locale, "recoveryMethods.speedUp.title")}
        </div>
        <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "12px" }}>
          {t(locale, "recoveryMethods.speedUp.description")}
        </div>

        {/* Security Question (method) */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text)", marginBottom: "4px" }}>{t(locale, "recoveryMethods.question.title")}</div>
            <div style={{ fontSize: "12px", color: "var(--muted)" }}>
              {hasSecurityQuestion ? t(locale, "recoveryMethods.question.configured") : t(locale, "recoveryMethods.notConfigured")}
            </div>
          </div>
          <button
            onClick={() => setQuestionModalOpen(true)}
            style={hasSecurityQuestion ? btnGhost : btnAccent}
            onMouseEnter={(e) => {
              if (!hasSecurityQuestion) {
                e.currentTarget.style.opacity = "0.9";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
          >
            {hasSecurityQuestion ? t(locale, "common.change") : t(locale, "common.set")}
          </button>
        </div>
      </div>

      {/* Recovery History Divider */}
      <div style={{ height: "1px", background: "var(--line)", margin: "16px 0", opacity: 0.5 }} />

      {/* Recovery History Subsection */}
      <div style={{ marginTop: "16px" }}>
        <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted)", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          {t(locale, "recoveryMethods.history.title")}
        </div>

        {attempts.length === 0 ? (
          <div style={{ fontSize: "13px", color: "var(--muted)" }}>
            {t(locale, "recoveryMethods.history.empty")}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {attempts.map((attempt) => (
              <div key={attempt.id} style={{ fontSize: "13px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                  <span style={{ color: "var(--text)", fontWeight: 500, minWidth: "100px" }}>{attempt.date}</span>
                  <span style={{ color: "var(--text)" }}>{attempt.method}</span>
                  <span
                    style={{
                      color: getStatusColor(attempt.status),
                      fontWeight: 500
                    }}
                  >
                    {attempt.status.charAt(0).toUpperCase() + attempt.status.slice(1)}
                  </span>
                  {attempt.isAnomalous && (
                    <span style={{ color: "#f59e0b", fontSize: "12px", marginLeft: "4px", fontWeight: 600 }}>!</span>
                  )}
                </div>
                <div style={{ color: "var(--muted)", fontSize: "12px", paddingLeft: "100px" }}>{attempt.location}</div>
              </div>
            ))}
            {attempts.length > 0 && (
              <div style={{ fontSize: "12px", color: "var(--muted)", marginTop: "4px" }}>
                {t(locale, "recoveryMethods.history.noOlder")}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <CardSetupModal
        isOpen={cardModalOpen}
        onClose={() => setCardModalOpen(false)}
        onSuccess={(result) => {
          setCardAnchor({
            status: "active",
            brand: result.brand,
            last4: result.last4
          });
        }}
      />

      <RecoveryEmailModal
        isOpen={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        onSuccess={() => {
          // A new (pending) destination was registered — reload to pick up the
          // masked value and pending badge from the server.
          loadData();
        }}
      />

      <RecoveryPhoneModal
        isOpen={phoneModalOpen}
        onClose={() => setPhoneModalOpen(false)}
        onSuccess={() => {
          // Phone was registered AND verified in the modal — reload the list.
          loadData();
        }}
      />

      <SecurityQuestionModal
        isOpen={questionModalOpen}
        onClose={() => setQuestionModalOpen(false)}
        onSuccess={() => {
          setHasSecurityQuestion(true);
        }}
      />

      <ConfirmPasswordModal
        isOpen={removeTarget !== null}
        locale={locale}
        title={t(locale, "recoveryMethods.destination.removeTitle")}
        description={
          removeTarget?.type === "sms"
            ? t(locale, "recoveryMethods.destination.removePhoneDescription")
            : t(locale, "recoveryMethods.destination.removeEmailDescription")
        }
        confirmLabel={t(locale, "common.remove")}
        onConfirm={() => {
          if (removeTarget) {
            const target = removeTarget;
            setRemoveTarget(null);
            handleRemoveDestination(target);
          }
        }}
        onClose={() => setRemoveTarget(null)}
      />
    </div>
  );
}
